import React, { useState } from "react";
import { FaCheck as Check, FaCopy as Copy } from "react-icons/fa";
import QRCode from "react-qr-code";
import { generateVoucherDeeplinkUrl } from "src/utils/util";

type VoucherQrCardProps = {
  code: string;
  title?: string;
  size?: number;
  className?: string;
};

const VOUCHER_LINK_TEXT = "Apply voucher code";

export const VoucherQrCard: React.FC<VoucherQrCardProps> = ({
  code,
  title = "Voucher QR Code",
  size = 128,
  className = "",
}) => {
  const [copied, setCopied] = useState(false);
  const voucherUrl = generateVoucherDeeplinkUrl(code);

  const handleCopyLink = async () => {
    const htmlLink = `<a href="${voucherUrl}">${VOUCHER_LINK_TEXT}</a>`;
    const markdownLink = `[${VOUCHER_LINK_TEXT}](${voucherUrl})`;

    if (navigator.clipboard.write && window.ClipboardItem) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlLink], { type: "text/html" }),
          "text/plain": new Blob([markdownLink], { type: "text/plain" }),
        }),
      ]);
    } else {
      await navigator.clipboard.writeText(markdownLink);
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-800 ${className}`.trim()}>
      <div className="flex flex-col items-center gap-2 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">{title}</p>

        <div
          className="rounded-lg border border-gray-200 p-2 shadow-inner dark:border-gray-600"
          style={{ backgroundColor: "#FFFFFF" }}
        >
          <QRCode
            value={voucherUrl}
            size={size}
            bgColor="#FFFFFF"
            fgColor="#111111"
            style={{ backgroundColor: "#FFFFFF", height: "auto", maxWidth: "100%", width: "100%" }}
          />
        </div>

        <div className="flex max-w-full items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 shadow-sm dark:border-blue-700 dark:bg-blue-950">
          <a
            href={voucherUrl}
            target="_blank"
            rel="noreferrer"
            className="truncate text-xs font-semibold text-blue-700 underline decoration-blue-200 underline-offset-2 hover:text-blue-800 dark:text-blue-200 dark:decoration-blue-700 dark:hover:text-blue-100"
            title={voucherUrl}
          >
            {VOUCHER_LINK_TEXT}
          </a>

          <button
            type="button"
            onClick={handleCopyLink}
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-white shadow-sm transition-colors ${
              copied
                ? "border-emerald-500 bg-emerald-600 hover:bg-emerald-700 dark:border-emerald-400 dark:bg-emerald-500 dark:hover:bg-emerald-400"
                : "border-blue-600 bg-blue-600 hover:bg-blue-700 dark:border-blue-400 dark:bg-blue-500 dark:hover:bg-blue-400"
            }`}
            title={copied ? "Copied" : "Copy link"}
            aria-label={copied ? "Copied" : "Copy voucher link"}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
};
